import { useState, useEffect, useCallback } from "react";
import { api, formatApiError, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wand2, Sparkles, Check, Loader2 } from "lucide-react";

const CATEGORY_TONE = {
    "Food & Dining": "bg-rose-50 text-rose-800 border-rose-200",
    "Travel":        "bg-sky-50 text-sky-800 border-sky-200",
    "Hotel":         "bg-sky-50 text-sky-800 border-sky-200",
    "Fuel":          "bg-orange-50 text-orange-800 border-orange-200",
    "Subscription":  "bg-violet-50 text-violet-800 border-violet-200",
    "IT / Software": "bg-violet-50 text-violet-800 border-violet-200",
    "Shopping":      "bg-amber-50 text-amber-900 border-amber-200",
    "Utility Bills": "bg-stone-100 text-stone-800 border-stone-200",
    "Medical":       "bg-emerald-50 text-emerald-800 border-emerald-200",
    "Insurance":     "bg-indigo-50 text-indigo-800 border-indigo-200",
    "Personal Expense": "bg-teal-50 text-teal-800 border-teal-200",
    "Other":         "bg-stone-100 text-stone-700 border-stone-200",
};

export default function AutoCategorizeButton({ onDone }) {
    const [pending, setPending] = useState(0);
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState("idle"); // idle | previewing | reviewing | applying
    const [suggestions, setSuggestions] = useState([]);
    const [busy, setBusy] = useState(false);

    const loadPending = useCallback(async () => {
        try {
            const { data } = await api.get("/expenses/ai/pending-count");
            setPending(data?.count || 0);
        } catch { /* silent */ }
    }, []);
    useEffect(() => { loadPending(); }, [loadPending]);

    const startPreview = async () => {
        setOpen(true);
        setStep("previewing");
        setBusy(true);
        try {
            const { data } = await api.post("/expenses/ai/preview", { limit: 50 });
            setSuggestions(data.suggestions || []);
            setStep("reviewing");
            if (!data.suggestions?.length) {
                toast.info("Nothing new to categorize.");
            }
        } catch (err) {
            toast.error(formatApiError(err));
            setOpen(false); setStep("idle");
        } finally { setBusy(false); }
    };

    const commit = async () => {
        setStep("applying"); setBusy(true);
        try {
            const { data } = await api.post("/expenses/ai/apply", {
                limit: 200, min_confidence: 0.6, learn: true,
            });
            const total = data.applied_total || 0;
            toast.success(`Categorized ${total} · learned ${data.rules_learned} rules for next time`);
            setOpen(false); setStep("idle");
            loadPending();
            onDone && onDone();
        } catch (err) {
            toast.error(formatApiError(err));
            setStep("reviewing");
        } finally { setBusy(false); }
    };

    if (pending === 0) return null;

    return (
        <>
            <Button
                onClick={startPreview}
                variant="outline"
                className="border-violet-300 text-violet-800 hover:bg-violet-50"
                data-testid="expenses-auto-categorize-btn"
            >
                <Wand2 size={13} className="mr-1.5" />
                Auto-categorize {pending}
            </Button>

            <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
                <DialogContent className="max-w-2xl" data-testid="expenses-ac-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl flex items-center gap-2">
                            <Sparkles size={18} className="text-violet-700" />
                            AI category suggestions
                        </DialogTitle>
                        <DialogDescription className="text-[13px] text-stone-500">
                            One LLM call, up to 50 uncategorized expenses at a time. Review the
                            top matches &mdash; apply only if you&rsquo;re happy. Suggestions above 60%
                            confidence will be saved when you click Apply.
                        </DialogDescription>
                    </DialogHeader>

                    {step === "previewing" && (
                        <div className="py-10 text-center text-sm text-stone-500 flex flex-col items-center gap-3">
                            <Loader2 size={20} className="animate-spin text-violet-700" />
                            Sending {Math.min(pending, 50)} expenses to the classifier…
                        </div>
                    )}

                    {step === "reviewing" && suggestions.length > 0 && (
                        <div className="max-h-[55vh] overflow-y-auto thin-scroll space-y-1.5 -mx-6 px-6">
                            {suggestions.map((s) => {
                                const tone = CATEGORY_TONE[s.category] || CATEGORY_TONE.Other;
                                const low = s.confidence < 0.6;
                                return (
                                    <div
                                        key={s.id}
                                        className={`flex items-center gap-3 rounded-md px-3 py-2.5 border ${
                                            low ? "bg-stone-50 border-stone-200 opacity-70" : "bg-white border-stone-200"
                                        }`}
                                        data-testid={`ac-suggest-${s.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-stone-900 truncate">
                                                {s.merchant}
                                                <span className="text-[11px] text-stone-500 ml-2 tabular-nums">{fmtMoney(s.amount)}</span>
                                            </div>
                                            {s.reason && (
                                                <div className="text-[11px] text-stone-500 truncate">{s.reason}</div>
                                            )}
                                        </div>
                                        <span className={`text-[11px] px-2 py-0.5 rounded-full border shrink-0 ${tone}`}>
                                            {s.category}
                                        </span>
                                        <div className="text-[11px] text-stone-500 tabular-nums shrink-0 w-10 text-right">
                                            {Math.round((s.confidence || 0) * 100)}%
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {step === "reviewing" && suggestions.length === 0 && (
                        <div className="py-10 text-center text-sm text-stone-500">
                            No suggestions returned. Try re-running or adjust the merchant names.
                        </div>
                    )}

                    {step === "applying" && (
                        <div className="py-10 text-center text-sm text-stone-500 flex flex-col items-center gap-3">
                            <Loader2 size={20} className="animate-spin text-violet-700" />
                            Applying categories & learning merchant rules…
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                        <Button
                            onClick={commit}
                            disabled={busy || step !== "reviewing" || suggestions.length === 0}
                            className="bg-violet-700 hover:bg-violet-800 text-white"
                            data-testid="ac-apply-btn"
                        >
                            <Check size={13} className="mr-1.5" />
                            Apply {suggestions.filter((s) => (s.confidence || 0) >= 0.6).length}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
